import { useContext, useEffect, useState as useClassicState } from 'react';

import ReactTooltip from 'react-tooltip';

import { Icon as BlueIcon } from '@blueprintjs/core';

import Rolling from '@assets/gif/rolling.gif';
import ProductImage from '@assets/png/product.png';

import { CreateUpdateContext } from '@components/CreateUpdatesDialog/CreateUpdatesDialog.query';
import { CustomPickerAsyncPaginate } from '@components/CustomPickerWithBorder/CustomPicker';
import { useState } from '@hookstate/core';
import { IProductCreateUpdate } from '@models/Product.model';
import { getProductsCreateUpdate } from '@services/queries/products';
import { useInView } from 'react-intersection-observer';
import { useInfiniteQuery } from 'react-query';

import { RelatedTagsListWithTagDialog } from '@pages/Updates/components/UpdatesNew/components/RelatedTagsList/RelatedTagsListWithTagDialog';
import { getManufacturersCreateUpdate } from '@services/queries/manufacturers';
import { Menu } from '../../../SelectedItems/Menu/Menu';
import {
  Button,
  CheckBox,
  CheckBoxDiv,
  Container,
  Content,
  Divider,
  HelpToolTipTextDiv,
  InputContainer,
  InputSearch,
  LabelText,
  LoadingImg,
  LoadingText,
  LoadMore,
  ProductImg,
  Row,
  RowLeft,
  SearchCloseIconContainer,
  SearchIconContainer,
  SmallTickContainer,
  TableContent,
  TableHeader,
} from './ProductList.styles';

import { ProductListLoading } from './ProductList.loading';
import { Warning } from './Warning/Warning';

const ProductList = () => {
  const {
    setSelectedProducts,
    setProductManufacturerValue,
    searchProducts,
    selectedMenufacturers,
    selectedProducts,
    productManufacturerValue,
  } = useContext(CreateUpdateContext);

  const search = useState('');
  const [openWarning, setOpenWarning] = useClassicState<boolean>(false);
  const [eventValue, setEventValue] = useClassicState<any>();

  const [manufacturerValue, setManufacturerValue] = useClassicState<{
    value: string;
    label: string;
  } | null>(productManufacturerValue);

  const { ref, inView } = useInView();

  const manufacturersQuery = useInfiniteQuery(
    ['createUpdate', 'manufacturers'],
    async ({ pageParam = 1 }) => {
      const studioUpdates = await getManufacturersCreateUpdate({
        page: pageParam,
        limit: 20,
        search: '',
      });

      return studioUpdates;
    },
    {
      getNextPageParam: lastPage =>
        lastPage.hasMore ? lastPage.currentPage + 1 : undefined,
    },
  );

  const loadOptions = (s: any, prevOptions: any) => {
    manufacturersQuery.fetchNextPage();

    const options =
      manufacturersQuery.data?.pages
        .map(pageData => pageData.results)
        .reduce(function reduce(prev, next) {
          return prev.concat(next);
        })
        .map(item => ({ value: item.id, label: item.name })) || [];

    let filteredOptions;

    if (!s) {
      filteredOptions = options;
    } else {
      const searchLower = s.toLowerCase();

      filteredOptions = options.filter(({ label }) =>
        label.toLowerCase().includes(searchLower),
      );
    }

    const slicedOptions = filteredOptions.slice(
      prevOptions.length,
      prevOptions.length + 20,
    );

    return {
      options: slicedOptions,
      hasMore: manufacturersQuery.data
        ? manufacturersQuery.data.pages[
            manufacturersQuery.data.pages.length - 1
          ].hasMore
        : false,
    };
  };

  const { data, isLoading, fetchNextPage } = useInfiniteQuery(
    [
      'createUpdate',
      'products',
      searchProducts.get(),
      manufacturerValue?.value,
    ],
    async ({ pageParam = 1 }) => {
      const studioUpdates = await getProductsCreateUpdate({
        page: pageParam,
        limit: 20,
        search: searchProducts.get(),
        manufacturerId: manufacturerValue?.value,
      });

      return studioUpdates;
    },
    {
      getNextPageParam: lastPage =>
        lastPage.hasMore ? lastPage.currentPage + 1 : undefined,
    },
  );

  const hasMore = data ? data.pages[data.pages.length - 1].hasMore : false;

  useEffect(() => {
    if (inView && hasMore) fetchNextPage();
  }, [inView]);

  const toggleSelectedManufacturer = (product: IProductCreateUpdate) => {
    const findIndex = selectedProducts.findIndex(
      item => item.id === product.id,
    );

    if (findIndex !== -1) {
      setSelectedProducts(
        selectedProducts.filter(item => item.id !== product.id),
      );
    } else {
      setSelectedProducts([...selectedProducts, product]);
    }
  };

  const products = data?.pages
    .map(pageData => pageData.results)
    .reduce(function reduce(prev, next) {
      return prev.concat(next);
    });
  const allSelected =
    products?.length === selectedProducts.length && products?.length !== 0;

  const selectAll = () => {
    if (allSelected) setSelectedProducts([]);
    else if (products) setSelectedProducts(products);
  };

  const onClickFunction = () => {
    setProductManufacturerValue(manufacturerValue);
  };

  return (
    <Container>
      <Row className="search-selectedItems">
        <RowLeft>
          <LabelText isActive>This update will be added to:</LabelText>
          <Divider width="16px" />
          <div data-tip data-for="manufacturerTooltip">
            <CustomPickerAsyncPaginate
              value={manufacturerValue}
              loadOptions={loadOptions}
              onChange={e => {
                setEventValue(e);
                if (
                  e?.value !== productManufacturerValue?.value &&
                  manufacturerValue?.value &&
                  selectedProducts.length > 0
                ) {
                  setOpenWarning(true);
                } else setManufacturerValue(e);
              }}
              width={216}
              placeholder="Select a Manufacturer"
              isClearable
              isDisabled={isLoading || !data}
            />
          </div>
          <ReactTooltip
            className="tooltip"
            id="manufacturerTooltip"
            effect="solid"
            multiline
          >
            <HelpToolTipTextDiv>
              As of now, updates can only be created for products from one
              manufacturer.
            </HelpToolTipTextDiv>
          </ReactTooltip>
          <Divider width="8px" />
          <InputContainer>
            <SearchIconContainer>
              <BlueIcon icon="search" size={12} color="#8F95B2" />
            </SearchIconContainer>
            <InputSearch
              placeholder="Search"
              disabled={!manufacturerValue}
              value={search.get()}
              onChange={e => search.set(e.target.value)}
              onKeyDown={e => {
                if (e.key === 'Enter') {
                  searchProducts.set(search.get());
                }
              }}
            />
            {searchProducts.get() && (
              <SearchCloseIconContainer
                onClick={() => {
                  searchProducts.set('');
                  search.set('');
                }}
              >
                <BlueIcon icon="small-cross" color="#8F95B2" />
              </SearchCloseIconContainer>
            )}
          </InputContainer>
        </RowLeft>

        <Button type="button">
          <Menu
            manufacturers={selectedMenufacturers}
            products={selectedProducts}
          />
        </Button>
      </Row>

      <Divider />

      <Content>
        <TableHeader>
          <Row>
            <CheckBox
              onClick={() => {
                selectAll();
                onClickFunction();
              }}
              disabled={!manufacturerValue?.value}
            >
              <CheckBoxDiv border={allSelected}>
                {allSelected && (
                  <SmallTickContainer>
                    <BlueIcon icon="small-tick" color="#fff" />
                  </SmallTickContainer>
                )}
              </CheckBoxDiv>
            </CheckBox>
            <LabelText className="tabble-header">Product Name</LabelText>
          </Row>
          <LabelText className="tabble-header">Model #</LabelText>
          <LabelText className="tabble-header">Related Tags</LabelText>
          <LabelText className="tabble-header" limitOfLines={1}>
            Number of Assets
          </LabelText>
        </TableHeader>

        {isLoading && !data ? (
          <ProductListLoading />
        ) : (
          <>
            {manufacturerValue?.value ? (
              <>
                {products?.map(productItem => {
                  const selected = !!selectedProducts.find(
                    item => item.id === productItem.id,
                  );

                  return (
                    <TableContent>
                      <Row>
                        <CheckBox
                          onClick={() => {
                            toggleSelectedManufacturer(productItem);
                            onClickFunction();
                          }}
                        >
                          <CheckBoxDiv border={selected}>
                            {selected && (
                              <SmallTickContainer>
                                <BlueIcon icon="small-tick" color="#fff" />
                              </SmallTickContainer>
                            )}
                          </CheckBoxDiv>
                        </CheckBox>
                        <ProductImg src={productItem.image || ProductImage} />
                        <LabelText className="tabble-row" limitOfLines={1}>
                          {productItem.name}
                        </LabelText>
                      </Row>
                      <LabelText className="tabble-row" limitOfLines={1}>
                        {productItem.model}
                      </LabelText>
                      <RelatedTagsListWithTagDialog tags={productItem.tags} />
                      <LabelText className="tabble-row" limitOfLines={1}>
                        {productItem.equipmentsCount}
                      </LabelText>
                    </TableContent>
                  );
                })}

                <LoadMore ref={ref}>
                  {hasMore ? (
                    <>
                      <LoadingText>Loading more</LoadingText>
                      <LoadingImg src={Rolling} alt="loading" />
                    </>
                  ) : (
                    <>
                      <LoadingText>End of List</LoadingText>
                    </>
                  )}
                </LoadMore>
              </>
            ) : (
              <div />
            )}
          </>
        )}

        <Warning
          open={openWarning}
          onClose={() => {
            setOpenWarning(false);
          }}
          setManufacturerValue={setManufacturerValue}
          eventValue={eventValue}
          setSelectedProducts={setSelectedProducts}
        />
      </Content>
    </Container>
  );
};

export { ProductList };
